import { memo, useMemo } from 'react';
import type { BoardNotation } from '@draughts/shared';
import { squareToAlgebraic } from '@draughts/core';
import { positionToSquare, isPlayableSquare } from '../../utils/board-coordinates';
import './BoardCoords.css';

/**
 * Come la scacchiera ospite realizza il ribaltamento — e quindi cosa deve fare
 * l'etichetta per restare leggibile e nell'angolo giusto.
 *
 * - `mirror`: le coordinate sono specchiate in JS e l'SVG non ruota (EditorBoard,
 *   GameReplay). L'etichetta si disegna nel punto già specchiato, dritta.
 * - `rotate`: l'INTERO <svg> è ruotato di 180° via CSS (GameBoard, `.game-board.flipped`).
 *   Lì l'etichetta va contro-ruotata come fa ogni pezzo, altrimenti si legge a testa in
 *   giù; e il suo ancoraggio va preso all'angolo OPPOSTO della casella, perché dopo le
 *   due rotazioni è quello che finisce in alto a sinistra di ciò che si vede.
 */
export type BoardCoordsFlipMode = 'mirror' | 'rotate';

interface BoardCoordsProps {
  notation: BoardNotation;
  boardSize: number;
  /** Lato di una casella nelle unità del viewBox della scacchiera ospite (40/60/90). */
  squareSize: number;
  darkSquareBottomRight: boolean;
  /** Varianti a caselle tutte giocabili (turca, armena): numerazione 1..N² per righe. */
  allSquares: boolean;
  flipped?: boolean;
  flipMode?: BoardCoordsFlipMode;
  /**
   * Ritaglio: quante righe e colonne disegnare a partire dall'angolo in alto a
   * sinistra, se l'ospite non e' una scacchiera intera. Serve all'anteprima delle
   * impostazioni, che e' un riquadro 6x2 e non una 10x10.
   *
   * La NUMERAZIONE resta quella di `boardSize`: il ritaglio e' un angolo di scacchiera
   * vera, quindi le etichette sono i numeri veri di quelle caselle e non una finzione.
   *
   * Solo per ospiti NON ribaltabili: entrambi i modi di `flipped` misurano dal bordo
   * lontano della scacchiera intera, che in un ritaglio non c'e'.
   */
  rows?: number;
  cols?: number;
}

// Frazioni del lato della casella, non pixel: le scacchiere disegnano la stessa
// geometria a scale diverse (34, 40, 56, 60, 90 unità), e l'etichetta deve pesare uguale
// su tutte.
const FONT = 0.24;
const INSET = 0.04;
// Stesso rapporto col font di prima (~12,5%): tenuto a 0.04 l'alone chiuderebbe
// l'occhiello di uno 0 o di un 8 alla nuova misura.
const STROKE = 0.03;

/** Gli attributi SVG sono stringhe: senza questo `0.24 * 90` finirebbe come
 *  `21.599999999999998` nel markup — e nelle asserzioni dei test. */
function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/**
 * Le etichette delle caselle — numero (`31`) o coordinata algebrica (`c3`) — disegnate
 * SOTTO i pezzi, nell'angolo in alto a sinistra di ogni casella giocabile.
 *
 * L'etichetta annota la casella, non il pezzo: a questa misura vive quasi tutta
 * nell'angolo che il tondo del pezzo (raggio 0.40 del lato) non raggiunge, quindi stare
 * sotto non la nasconde — mentre stare sopra significava coprire i pezzi. Dove sfiora
 * davvero, sfiora e basta: è la casella a parlare, non l'annotazione.
 *
 * Due eccezioni note, accettate: le forme di pezzo `quadrato` e `rombo` invadono
 * l'angolo, e in `algebraic` le traverse dalla 10 in su fanno un'etichetta di TRE
 * caratteri (`b10`) la cui coda finisce sotto il pezzo. In `numeric` il caso non esiste:
 * il numero più alto di tutte le varianti è 98 (sudafricana 14x14), cioè due cifre.
 *
 * Va montato nell'<svg> DOPO le evidenziazioni di casella e PRIMA dei pezzi: così i
 * numeri restano nitidi sopra gli aloni e sopra l'anello di presa, ma sotto i pezzi.
 *
 * `memo`: i prop sono tutti primitivi e cambiano solo quando cambia la variante, la
 * preferenza o il verso della scacchiera — mentre l'ospite (GameBoard) si ridisegna a
 * ogni frame di trascinamento, e queste sono fino a 98 <text>.
 */
function BoardCoords({
  notation,
  boardSize,
  squareSize,
  darkSquareBottomRight,
  allSquares,
  flipped = false,
  flipMode = 'mirror',
  rows,
  cols,
}: BoardCoordsProps) {
  const nRows = rows ?? boardSize;
  const nCols = cols ?? boardSize;
  const labels = useMemo(() => {
    if (notation === 'none') return [];
    const out: { key: string; text: string; x: number; y: number; dark: boolean; rotate: boolean }[] = [];
    for (let row = 1; row <= nRows; row++) {
      for (let col = 1; col <= nCols; col++) {
        // `null` = casella non numerata, cioè le chiare delle varianti a scacchiera.
        // Nelle varianti a caselle tutte giocabili non è mai null: lì si numera tutto.
        const square = positionToSquare({ row, col }, boardSize, darkSquareBottomRight, allSquares);
        if (square === null) continue;
        const text = notation === 'numeric'
          ? String(square)
          : squareToAlgebraic(square, boardSize, darkSquareBottomRight, allSquares);
        const rotate = flipped && flipMode === 'rotate';
        // In `rotate` l'ancoraggio è l'angolo in basso a destra della casella: le due
        // rotazioni (SVG + contro-rotazione) lo riportano esattamente dove sta l'angolo
        // in alto a sinistra nella vista dritta.
        const x = round(rotate
          ? col * squareSize - INSET * squareSize
          : ((flipped && flipMode === 'mirror' ? boardSize - col : col - 1) * squareSize) + INSET * squareSize);
        const y = round(rotate
          ? row * squareSize - FONT * squareSize
          : ((flipped && flipMode === 'mirror' ? boardSize - row : row - 1) * squareSize) + FONT * squareSize);
        out.push({ key: `${row}-${col}`, text, x, y, dark: isPlayableSquare(row, col, boardSize, darkSquareBottomRight), rotate });
      }
    }
    return out;
  }, [notation, boardSize, squareSize, darkSquareBottomRight, allSquares, flipped, flipMode, nRows, nCols]);

  if (labels.length === 0) return null;

  return (
    <g className="board-coords" aria-hidden="true">
      {labels.map((l) => (
        <text
          key={l.key}
          className={`board-coord ${l.dark ? 'on-dark' : 'on-light'}`}
          x={l.x}
          y={l.y}
          fontSize={round(FONT * squareSize)}
          strokeWidth={round(STROKE * squareSize)}
          transform={l.rotate ? `rotate(180 ${l.x} ${l.y})` : undefined}
        >
          {l.text}
        </text>
      ))}
    </g>
  );
}

export default memo(BoardCoords);
